import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, EmptyState, Field } from "../components/ui-ext";
import StatusPill from "../components/StatusPill";
import { Plus, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "../components/ExportButton";

export default function LeavesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/leaves", { params: { status: filter || undefined } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const isEng = user.role === "engineer";

  return (
    <div>
      <PageHeader
        eyebrow="Leave"
        title={isEng ? "My Leaves" : "Team Leaves"}
        subtitle="Casual, sick, earned & unpaid leave requests."
        right={
          <div className="flex items-center gap-2">
            {!isEng && <ExportButton endpoint="leaves.xlsx" testid="export-leaves-btn" />}
            {isEng && <button onClick={() => setShow(true)} className="bg-[#FF5722] hover:bg-[#F4511E] px-4 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="new-leave-btn"><Plus className="w-4 h-4" /> Request</button>}
          </div>
        }
      />

      <Card>
        {items.length === 0 ? <EmptyState title="No leave requests" /> : (
          <div className="overflow-x-auto">
            <table className="w-full tech-table">
              <thead><tr>
                <th>From</th><th>To</th>{!isEng && <th>Engineer</th>}<th>Type</th><th>Reason</th><th>Status</th>
              </tr></thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id}>
                    <td className="mono">{l.from_date}</td>
                    <td className="mono">{l.to_date}</td>
                    {!isEng && <td>{l.engineer_name}</td>}
                    <td className="capitalize">{l.leave_type}</td>
                    <td className="text-xs text-slate-400 max-w-xs truncate">{l.reason}</td>
                    <td><StatusPill status={l.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {show && <LeaveDialog onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
    </div>
  );
}

function LeaveDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    leave_type: "casual",
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/leaves", form);
      toast.success("Leave requested");
      onCreated();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-md">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Request Leave</h2>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="From"><input type="date" className="input" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} data-testid="leave-from" /></Field>
            <Field label="To"><input type="date" className="input" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} data-testid="leave-to" /></Field>
          </div>
          <Field label="Type">
            <select className="input" value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} data-testid="leave-type">
              <option value="casual">Casual</option>
              <option value="sick">Sick</option>
              <option value="earned">Earned</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </Field>
          <Field label="Reason"><textarea rows={3} className="input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-reason" /></Field>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="bg-[#FF5722] hover:bg-[#F4511E] px-5 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="leave-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit
          </button>
        </div>
        <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
      </div>
    </div>
  );
}
